import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom-v5-compat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH';

import { Button } from 'ui-components/components/button';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Tag } from 'ui-components/components/tag';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import styles from './labs-feature-table-row.module.less';
import { CATEGORY_MESSAGE_MAP, CATEGORY_TAG_TYPE_MAP } from '../../labs-feature-categories';
import { CTA_ACTION_PREFIX, LABS_CTA_ACTION_EVENT } from '../../labs-feature-constants';
import { featureMessages } from '../../labs-feature-messages';
import { messages } from '../../messages';
import { Features } from '../../../../../constants/features';
import { useFeatureFlag } from '../../../../../feature-flags/hooks/use-feature-flag';
import { FeatureFlagDeclarations } from '../../../../../feature-flags/feature-flag-declarations';

const useIsUnderMaintenance = (featureId) => {
  const isHarmonizationSuggestionEnabled = useFeatureFlag(FeatureFlagDeclarations.enableAiHarmonizationCodeGenerator);
  const isAiShippyAssistantEnabled = useFeatureFlag(FeatureFlagDeclarations.enableAiShippyAssistant);
  const isAutomationRuleCreatorEnabled = useFeatureFlag(FeatureFlagDeclarations.enableAIAutomationRule);

  if (featureId === Features.AiHarmonizationCodeGenerator) {
    return !isHarmonizationSuggestionEnabled;
  }

  if (featureId === Features.AiShippyAssistant) {
    return !isAiShippyAssistantEnabled;
  }

  if (featureId === Features.AiAutomationRuleCreator) {
    return !isAutomationRuleCreatorEnabled;
  }

  return false;
};

const useIsHidden = (featureId) => {
  const isPackingRecommendationEnabled = useFeatureFlag(FeatureFlagDeclarations.enablePackingRecommendation);

  if (featureId === Features.PackingRecommendations) {
    return !isPackingRecommendationEnabled;
  }

  return false;
};

/**
 * Labs feature table row component
 * @param {object} props - Component props
 * @param {import('../../typedefs').LabsFeature} props.feature - The feature data
 * @param {boolean} props.isEnabled - Whether the feature is currently enabled
 * @param {(feature: import('../../typedefs').LabsFeature, isEnabled: boolean) => void} props.onToggle - Function called when user toggles feature
 * @param {(feature: import('../../typedefs').LabsFeature) => void} props.onSendFeedback - Function called when user wants to send feedback
 * @param {(feature: import('../../typedefs').LabsFeature) => void} props.onViewDetails - Function called when user wants to view feature details
 * @returns {React.ReactElement} The feature table row component
 */
export const LabsFeatureTableRow = ({ feature, isEnabled, onToggle, onSendFeedback, onViewDetails }) => {
  const { titleKey, descriptionKey, category, ctaButtonTitleKey, ctaRoute } = feature;
  const categoryMessage = CATEGORY_MESSAGE_MAP[category];
  const categoryTagType = CATEGORY_TAG_TYPE_MAP[category];
  const isUnderMaintenance = useIsUnderMaintenance(feature.id);
  const isHidden = useIsHidden(feature.id);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const handleToggleChange = useCallback(
    (checked) => {
      onToggle(feature, !checked);
    },
    [feature, onToggle],
  );

  const handleViewDetailsClick = useCallback(() => {
    onViewDetails(feature);
  }, [feature, onViewDetails]);

  const handleSendFeedbackClick = useCallback(() => {
    onSendFeedback(feature);
  }, [feature, onSendFeedback]);

  const handleCtaClick = useCallback(() => {
    if (ctaRoute?.startsWith(CTA_ACTION_PREFIX)) {
      const actionId = ctaRoute.slice(CTA_ACTION_PREFIX.length);
      window.dispatchEvent(new CustomEvent(LABS_CTA_ACTION_EVENT, { detail: { actionId } }));
    } else if (ctaRoute) {
      navigate(ctaRoute);
    }
  }, [ctaRoute, navigate]);

  const hasCtaAction = isEnabled && ctaRoute && ctaButtonTitleKey;

  if (isHidden) {
    return null;
  }

  return (
    <div className={styles['table-row']}>
      <FlexColumn className={styles['name-cell']}>
        <Button
          ariaLabel={formatMessage(messages.viewDetailsButtonText)}
          className={styles['clickable-title']}
          onClick={handleViewDetailsClick}
          theme={THEME_CONSTANTS.unstyled}
        >
          <Text className={styles['feature-title']} textType={TextType.Paragraph} value={featureMessages[titleKey]} />
        </Button>
        <Text className={styles.description} textType={TextType.Caption} value={featureMessages[descriptionKey]} />
      </FlexColumn>

      <FlexRow className={styles['category-cell']}>
        {categoryTagType && categoryMessage ? (
          <Tag message={<FormattedMessage {...categoryMessage} />} showStatusDot={false} type={categoryTagType} />
        ) : null}
      </FlexRow>

      <FlexRow className={styles['status-cell']}>
        <FlexRow className={styles['status-container']}>
          <CheckboxSlider
            checkboxId={`labs-feature-${feature.id}`}
            checked={isEnabled && !isUnderMaintenance}
            disabled={isUnderMaintenance}
            disabledTooltipMessage={isUnderMaintenance ? <FormattedMessage {...messages.underMaintenance} /> : null}
            labelText={
              isEnabled && !isUnderMaintenance ? <FormattedMessage {...messages.enabled} /> : <FormattedMessage {...messages.disabled} />
            }
            onChange={handleToggleChange}
          />
        </FlexRow>
      </FlexRow>

      <FlexRow className={styles['actions-cell']}>
        <DropdownMenu menuPosition='bottom-right'>
          <DropdownMenuToggler
            ariaLabel={formatMessage(messages.openActionsMenu)}
            className={styles['menu-toggle']}
            hideChevron
            theme='link'
          >
            <FontAwesomeIcon icon={faEllipsisH} />
          </DropdownMenuToggler>
          {hasCtaAction ? (
            <DropdownMenuItem onClick={handleCtaClick}>
              <FormattedMessage {...featureMessages[ctaButtonTitleKey]} />
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={handleViewDetailsClick}>
            <FormattedMessage {...messages.viewDetailsButtonText} />
          </DropdownMenuItem>
          {isEnabled ? (
            <DropdownMenuItem onClick={handleSendFeedbackClick}>
              <FormattedMessage {...messages.sendFeedback} />
            </DropdownMenuItem>
          ) : null}
        </DropdownMenu>
      </FlexRow>
    </div>
  );
};
